import {
  Dropdown,
  Field,
  makeStyles,
  Option,
  SpinButton,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import {
  DEFAULT_CONDENSED_TOOLBAR_BUTTONS,
  EditorLabelPosition,
  RichEdit,
  RichEditToolbarButton,
  RichEditToolbarMode,
  Section,
  SectionColumn,
} from '@powerportalspro/react-fluent';
import { useState } from 'react';
import { useExampleDocument } from './use-example-document';

// Localization namespace — reuses the Blazor demo's
// `RichEditPropertiesDemo` keys so both stacks read from the same JSON.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors.RichEdit.RichEditPropertiesDemo';

// Every toolbar button, named as its tooltip names it.
const BUTTONS: readonly [RichEditToolbarButton, string][] = [
  [RichEditToolbarButton.Undo, 'undo'],
  [RichEditToolbarButton.Redo, 'redo'],
  [RichEditToolbarButton.FormatPainter, 'format-painter'],
  [RichEditToolbarButton.Font, 'font'],
  [RichEditToolbarButton.FontSize, 'font-size'],
  [RichEditToolbarButton.Bold, 'bold'],
  [RichEditToolbarButton.Italic, 'italic'],
  [RichEditToolbarButton.Underline, 'underline'],
  [RichEditToolbarButton.Strikethrough, 'strikethrough'],
  [RichEditToolbarButton.HighlightColor, 'highlight-color'],
  [RichEditToolbarButton.TextColor, 'text-color'],
  [RichEditToolbarButton.BulletedList, 'bullet-list'],
  [RichEditToolbarButton.NumberedList, 'numbered-list'],
  [RichEditToolbarButton.DecreaseIndent, 'decrease-indent'],
  [RichEditToolbarButton.IncreaseIndent, 'increase-indent'],
  [RichEditToolbarButton.Blockquote, 'blockquote'],
  [RichEditToolbarButton.AlignLeft, 'align-left'],
  [RichEditToolbarButton.AlignCenter, 'align-center'],
  [RichEditToolbarButton.AlignRight, 'align-right'],
  [RichEditToolbarButton.Link, 'link'],
  [RichEditToolbarButton.RemoveLink, 'remove-link'],
  [RichEditToolbarButton.Subscript, 'subscript'],
  [RichEditToolbarButton.Superscript, 'superscript'],
  [RichEditToolbarButton.Image, 'image'],
  [RichEditToolbarButton.LeftToRight, 'left-to-right'],
  [RichEditToolbarButton.RightToLeft, 'right-to-left'],
  [RichEditToolbarButton.RemoveFormatting, 'remove-format'],
  [RichEditToolbarButton.Table, 'insert-table'],
];

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  inlineField: {
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': {
      whiteSpace: 'nowrap',
    },
  },
  numberInput: {
    width: '65px',
  },
  buttonPicker: {
    minWidth: '220px',
    width: '220px',
  },
});

export function RichEditPropertiesSample() {
  useExampleDocument('ppp_richdescription');
  const styles = useStyles();
  const t = useT();
  const [readOnly, setReadOnly] = useState(false);
  const [required, setRequired] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [displayToolbar, setDisplayToolbar] = useState(true);
  const [condensedToolbar, setCondensedToolbar] = useState(false);
  const [condensedButtons, setCondensedButtons] = useState<readonly RichEditToolbarButton[]>(DEFAULT_CONDENSED_TOOLBAR_BUTTONS);
  // None chosen is every button.
  const [toolbarButtons, setToolbarButtons] = useState<readonly RichEditToolbarButton[]>([]);
  const [allowImages, setAllowImages] = useState(true);
  const [autoFitHeight, setAutoFitHeight] = useState(false);
  const [rows, setRows] = useState(8);

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Switch
              label={t(`${STRINGS_BASE}.readonly`)}
              labelPosition="before"
              checked={readOnly}
              onChange={(_, d) => setReadOnly(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.required`)}
              labelPosition="before"
              checked={required}
              onChange={(_, d) => setRequired(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.disabled`)}
              labelPosition="before"
              checked={disabled}
              onChange={(_, d) => setDisabled(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.display-toolbar`)}
              labelPosition="before"
              checked={displayToolbar}
              onChange={(_, d) => setDisplayToolbar(d.checked)}
            />
            {displayToolbar && (
              <Field
                label={t(`${STRINGS_BASE}.toolbar-buttons`)}
                orientation="horizontal"
                className={styles.inlineField}
              >
                <Dropdown
                  className={styles.buttonPicker}
                  multiselect
                  placeholder={t(`${STRINGS_BASE}.every-button`)}
                  selectedOptions={[...toolbarButtons]}
                  onOptionSelect={(_, d) => setToolbarButtons(d.selectedOptions as RichEditToolbarButton[])}
                >
                  {BUTTONS.map(([button, label]) => (
                    <Option key={button} value={button}>
                      {t(`app.components.rich-edit.${label}`)}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
            <Switch
              label={t(`${STRINGS_BASE}.condensed-toolbar`)}
              labelPosition="before"
              checked={condensedToolbar}
              onChange={(_, d) => setCondensedToolbar(d.checked)}
            />
            {condensedToolbar && (
              <Field
                label={t(`${STRINGS_BASE}.condensed-buttons`)}
                orientation="horizontal"
                className={styles.inlineField}
              >
                <Dropdown
                  className={styles.buttonPicker}
                  multiselect
                  placeholder={t(`${STRINGS_BASE}.default-buttons`)}
                  selectedOptions={[...condensedButtons]}
                  onOptionSelect={(_, d) => setCondensedButtons(d.selectedOptions as RichEditToolbarButton[])}
                >
                  {BUTTONS.map(([button, label]) => (
                    <Option key={button} value={button}>
                      {t(`app.components.rich-edit.${label}`)}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
            <Switch
              label={t(`${STRINGS_BASE}.allow-images`)}
              labelPosition="before"
              checked={allowImages}
              onChange={(_, d) => setAllowImages(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.auto-fit-height`)}
              labelPosition="before"
              checked={autoFitHeight}
              onChange={(_, d) => setAutoFitHeight(d.checked)}
            />
            <Field
              label={t(`${STRINGS_BASE}.num-rows`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <SpinButton
                className={styles.numberInput}
                min={1}
                value={rows}
                onChange={(_, d) => {
                  const n = typeof d.value === 'number' ? d.value : Number(d.displayValue);
                  if (Number.isFinite(n)) setRows(n);
                }}
              />
            </Field>
          </div>
        }
      >
        <RichEdit
          columnName="ppp_richdescription"
          labelPosition={EditorLabelPosition.Above}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          displayToolbar={displayToolbar}
          toolbarMode={condensedToolbar ? RichEditToolbarMode.Condensed : RichEditToolbarMode.Full}
          condensedToolbarButtons={condensedButtons}
          toolbarButtons={toolbarButtons}
          allowImages={allowImages}
          rows={rows}
          autoFitHeight={autoFitHeight}
          placeholder={t(`${STRINGS_BASE}.placeholder`)}
        />
      </SectionColumn>
    </Section>
  );
}
